import AdyenCheckout from '@adyen/adyen-web';
import { useEffect } from 'react';
import { TFunction } from 'react-i18next';

import { CreditCardBrand, Locale } from '../../../../types';
import { normalizeLocaleForAdyen } from '../../Adyen.static';
import { AdyenConfig } from '../../Adyen.types';
import {
  getAdyenCardBrands,
  overrideAdyenTranslations,
  RENDER_CONTAINER_ID,
  styles as adyenFormStyles,
} from '../AdyenCreditCardForm.static';

export interface AdyenSecureFieldsConfig extends AdyenConfig {
  locale: Locale;
  t: TFunction<'translation', undefined>;
  onChange: (state: unknown) => void;
  onFieldValid: (data: unknown) => void;
  onBrand: (brandData: unknown) => void;
  onFatalError: (error: unknown, hideForm?: boolean) => void;
  onFocus: (data: unknown) => void;
}

/**
 * Effect hook over the initialization of the 'securedfields' of AdyenCheckout.
 * The inputs 'encryptedCardNumber', 'encryptedExpiryMonth', 'encryptedExpiryYear' and 'encryptedSecurityCode' will be managed by Adyen once this hook finishes.
 */
const useAdyenSecureFields = (
  config: AdyenSecureFieldsConfig,
  supportedCreditCardBrands: CreditCardBrand[],
) => {
  const {
    adyenEnvironment,
    locale,
    t,
    clientKey,
    onChange,
    onFocus,
    onFieldValid,
    onBrand,
    onFatalError,
  } = config;

  useEffect(() => {
    async function initialize() {
      try {
        const adyenLocale = normalizeLocaleForAdyen(locale);
        const adyen = await AdyenCheckout({
          environment: adyenEnvironment,
          translations: { [adyenLocale]: overrideAdyenTranslations(t) },
          locale: adyenLocale,
          clientKey,
          onChange,
        });

        adyen
          .create('securedfields', {
            type: 'card',
            brands: getAdyenCardBrands(supportedCreditCardBrands),
            styles: adyenFormStyles,
            onBrand,
            onFieldValid,
            onFocus,
          })
          .mount(`#${RENDER_CONTAINER_ID}`);
      } catch (error) {
        onFatalError(error, false);
      }
    }

    initialize();
  }, [
    supportedCreditCardBrands,
    adyenEnvironment,
    locale,
    clientKey,
    onBrand,
    onFieldValid,
    onChange,
    onFatalError,
  ]);
};

export default useAdyenSecureFields;
